var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { DocumentRetrievalAuthType, DocumentRetrievalFailureReason, DocumentRetrievalLocationStatus, DocumentRetrievalTaskSource, DocumentVectorizationStatus, } from "healthex-common/document.retrieval.dtos";
import { computed, observable } from "mobx";
import { FastenCallbackEventType } from "../Infra/FastenLoaderService";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SmartAuthErrorReason, SmartAuthState } from "../Logic/SmartAuthManager";
import { FacilitatedFHIRAuthErrorReason, FacilitatedFHIRAuthState } from "./FacilitatedFHIRAuthManager";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
import { plainToInstance } from "class-transformer";
import { DocumentRetrievalUIState } from "./DocumentRetrievalUIState";
import { PatientConsentSessionState } from "./PatientConsentSession";
const t = getObservableT("patients");
export var DocumentRetrievalStep;
(function (DocumentRetrievalStep) {
    DocumentRetrievalStep["LOADING"] = "LOADING";
    DocumentRetrievalStep["RECORDS_FOUND"] = "RECORDS_FOUND";
    DocumentRetrievalStep["RECORDS_NOT_FOUND"] = "RECORDS_NOT_FOUND";
    DocumentRetrievalStep["ADD_AND_SHARE"] = "ADD_AND_SHARE";
    DocumentRetrievalStep["CONTINUE_WITHOUT_SHARING"] = "CONTINUE_WITHOUT_SHARING";
    DocumentRetrievalStep["TECHNICAL_ERROR"] = "TECHNICAL_ERROR";
    DocumentRetrievalStep["REVIEW_AND_SHARE"] = "REVIEW_LOCATIONS";
    DocumentRetrievalStep["LOCATION_LOGIN"] = "LOCATION_LOGIN";
    DocumentRetrievalStep["HUR_LOADING"] = "HUR_LOADING";
    DocumentRetrievalStep["RESTORING_SESSION"] = "RESTORING_SESSION";
})(DocumentRetrievalStep || (DocumentRetrievalStep = {}));
export class DocumentRetrievalViewModel extends PatientConsentViewModel {
    get uiState() {
        return this._uiState;
    }
    get retrievalError() {
        const retrieval = this.retrieval;
        if (!retrieval)
            return false;
        return this.documentRetrievalManager.retrievalErrors.some((retrievalError) => retrievalError.batchId === retrieval.batchId);
    }
    get isAIFlow() {
        var _a, _b;
        return (_b = (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.isAIFlow) !== null && _b !== void 0 ? _b : false;
    }
    get isVectorizationInProgress() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.IN_PROGRESS;
    }
    get isVectorizationComplete() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.COMPLETE;
    }
    get canAddLocations() {
        if (!!this.retrieval && this.retrieval.allowOnlyTargetedFacilities === true) {
            return false;
        }
        return true;
    }
    get zeroLocationsFound() {
        if (!!this.retrieval && this.retrieval.locations.length === 0) {
            return true;
        }
        return false;
    }
    get explanationText() {
        if (this.isDataUpdate) {
            if (this.zeroLocationsFound) {
                return t("To help you and {{ orgName }} access an updated copy of your health records, HealthEx connects to providers you have received care from.", { orgName: this.organizationName });
            }
            return t("To access an updated copy of your records, HealthEx has connected with the providers below. Some may ask you to log in, depending on their security settings.");
        }
        if (this.zeroLocationsFound) {
            return t("To help you and {{ orgName }} access your health records, HealthEx connects to providers you have received care from.", { orgName: this.organizationName });
        }
        return t("To help you and {{ orgName }} access your records, HealthEx has connected with the providers below. Some may ask you to log in, depending on their security settings.", { orgName: this.organizationName });
    }
    get needsToReauthWithCLEAR() {
        return false;
    }
    get getAuthErrorText() {
        const retrieval = this.retrieval;
        if (!retrieval)
            return t("Your CLEAR login has expired");
        const error = this.documentRetrievalManager.retrievalErrors.find((retrievalError) => retrievalError.batchId === retrieval.batchId);
        if (!error)
            return t("Your CLEAR login has expired");
        const isClearValidationFailed = error.medAlliesGetDocumentsError === DocumentRetrievalFailureReason.CLEAR_VALIDATION_FAILED ||
            error.medAlliesGetEndpointsError === DocumentRetrievalFailureReason.CLEAR_VALIDATION_FAILED;
        return isClearValidationFailed ? t("Your identity verification has expired.") : t("Your CLEAR login has expired");
    }
    get locationList() {
        if (this._uiState.staticRetrievalData) {
            return this._uiState.staticRetrievalData;
        }
        if (!this.retrieval)
            return [];
        const locations = this.retrieval.locations;
        const documents = this.retrieval.documents;
        return locations.map((location) => {
            var _a;
            const locationDocuments = documents.filter((document) => document.locationId === location.id);
            let locationStatus = location.status === DocumentRetrievalLocationStatus.ERROR ? DocumentRetrievalLocationStatus.COMPLETE : location.status;
            if (this._uiState.completedLoginKeys.includes(location.id)) {
                locationStatus = DocumentRetrievalLocationStatus.COMPLETE;
            }
            else if (this._uiState.skippedLoginKeys.includes(location.id)) {
                locationStatus = DocumentRetrievalLocationStatus.SKIPPED;
            }
            return {
                key: location.id,
                name: (_a = location.organizationName) !== null && _a !== void 0 ? _a : "",
                logoUrl: location.organizationLogoUrl,
                documents: locationDocuments.map((document) => {
                    var _a;
                    return ({
                        id: document.id,
                        filename: (_a = document.filename) !== null && _a !== void 0 ? _a : "",
                        source: document.source,
                        status: document.status,
                    });
                }),
                status: locationStatus,
                authType: location.authType,
                metadata: location.organizationMetadata,
            };
        });
    }
    get locationStatusCounts() {
        if (!this.retrieval)
            return {
                total: 0,
                completed: 0,
                error: 0,
                skipped: 0,
            };
        const locations = this.retrieval.locations;
        return {
            total: locations.length,
            completed: locations.filter((l) => l.status === DocumentRetrievalLocationStatus.COMPLETE).length,
            error: locations.filter((l) => l.status === DocumentRetrievalLocationStatus.ERROR).length,
            skipped: locations.filter((l) => l.status === DocumentRetrievalLocationStatus.SKIPPED).length,
        };
    }
    get isInHurLoading() {
        if (!this.retrieval)
            return false;
        if (!this.retrieval.documentSelectionComplete)
            return false;
        const isWaitingForFinalization = !this.retrieval.finalized && this.isIAS;
        return isWaitingForFinalization;
    }
    constructor(container) {
        super(container);
        this.previousTrackedStep = null;
        this.loadingStartTime = null;
        this._uiState = new DocumentRetrievalUIState();
        this.authManager = container.authManager;
        this.documentRetrievalManager = container.documentRetrievalManager;
        this.fastenLoaderService = container.fastenLoaderService;
        this.facilitatedFHIRAuthManager = container.facilitatedFHIRAuthManager;
        this.organizationsManager = container.organizationsManager;
        this.smartAuthManager = container.smartAuthManager;
        this.smartAuthWindow = container.smartAuthWindow;
        this.patientPreferencesManager = container.patientPreferencesManager;
    }
    run() {
        if (this._uiState.isStatic) {
            return;
        }
        this.fastenLoaderService.loadFasten();
        this.disposer.reaction(() => this.patientConsentUIManager.session.patientConsentSessionState, (patientConsentSessionState) => {
            if (patientConsentSessionState === PatientConsentSessionState.RESTORING_COMPLETED) {
                if (this.facilitatedFHIRAuthManager.hasCompletedAuth) {
                    this.restoreDocumentRetrievalUIState();
                }
                if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.SUCCESS) {
                    if (this._uiState.currentLocationKeyForLogin) {
                        this._uiState.completedLoginKeys.push(this._uiState.currentLocationKeyForLogin);
                    }
                    this.facilitatedFHIRAuthManager.resetAuth();
                }
                this.patientConsentUIManager.completeStateRestoration();
            }
        }, { fireImmediately: true, name: "FacilitatedFHIRAuthSuccess" });
        this.disposer.reaction(() => this.isLoading, (isLoading) => {
            if (isLoading) {
                this.startLoadingTimer();
            }
            else {
                this.clearLoadingTimer();
            }
        }, { name: "trackLoading", fireImmediately: true });
        this.disposer.reaction(() => this.currentStep, (step) => {
            this.trackScreenView(step);
        }, { name: "trackScreenView", fireImmediately: true });
    }
    stop() {
        this.disposer.dispose();
    }
    trackScreenView(step) {
        var _a, _b, _c;
        if (step === DocumentRetrievalStep.LOADING) {
            if (this.loadingStartTime === null) {
                this.loadingStartTime = Date.now();
                heap.track("DocumentRetrievalLoadingStart", {
                    patientId: this._uiState.patientId,
                    studyId: this._uiState.studyId,
                    batchId: (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId,
                });
            }
            this.previousTrackedStep = step;
            return;
        }
        if (this.previousTrackedStep === DocumentRetrievalStep.LOADING && this.loadingStartTime !== null) {
            const loadingDuration = Date.now() - this.loadingStartTime;
            const statusCounts = this.locationStatusCounts;
            heap.track("DocumentRetrievalLoadingComplete", {
                patientId: this._uiState.patientId,
                studyId: this._uiState.studyId,
                batchId: (_b = this.retrieval) === null || _b === void 0 ? void 0 : _b.batchId,
                durationSeconds: Math.round(loadingDuration / 1000),
                nextScreen: step,
                totalLocations: statusCounts.total,
                completedLocations: statusCounts.completed,
                errorLocations: statusCounts.error,
                skippedLocations: statusCounts.skipped,
                needsReauth: this.needsToReauthWithCLEAR,
                zeroLocationsFound: this.zeroLocationsFound,
                timestamp: new Date().toISOString(),
            });
            this.loadingStartTime = null;
        }
        if (step === this.previousTrackedStep) {
            return;
        }
        this.previousTrackedStep = step;
        const eventName = `DocumentRetrieval${this.toCamelCase(step)}`;
        heap.track(eventName, {
            patientId: this._uiState.patientId,
            studyId: this._uiState.studyId,
            batchId: (_c = this.retrieval) === null || _c === void 0 ? void 0 : _c.batchId,
            totalLocations: this.locationList.length,
            zeroLocationsFound: this.zeroLocationsFound,
            hasError: !!this.retrievalError,
        });
    }
    toCamelCase(step) {
        if (!step)
            return "Screen";
        return step
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join("");
    }
    get isProductionRetrieveDataEnabled() {
        return !this.authManager.isSandbox;
    }
    get facilitatedFhirAuthInProgress() {
        if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.IN_PROGRESS) {
            return this.facilitatedFHIRAuthManager.state.key;
        }
        else if (this._uiState.awaitingFacilitatedFHIRKey !== null) {
            return this._uiState.awaitingFacilitatedFHIRKey;
        }
        return null;
    }
    get isfacilitatedFhirAuthSuccessful() {
        return this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.SUCCESS;
    }
    get facilitatedFhirAuthError() {
        if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.ERROR) {
            return { key: this.facilitatedFHIRAuthManager.state.key, error: this.facilitatedFHIRAuthManager.state.error };
        }
        return null;
    }
    get facilitatedFhirAuthErrorMessage() {
        if (!this.facilitatedFhirAuthError)
            return "";
        switch (this.facilitatedFhirAuthError.error) {
            case FacilitatedFHIRAuthErrorReason.AUTH_WINDOW_CLOSED:
                return t("You closed the tab before we could find your records. Please try logging in again.");
            case FacilitatedFHIRAuthErrorReason.AUTH_FAILED:
                return t("You left the page before we could find your records. Please try logging in again.");
            default:
                return t("Something went wrong while finding your records. Please try logging in again.");
        }
    }
    isFeatureEnabled(featureFlag) {
        return this.authManager.isFeatureEnabled(featureFlag);
    }
    get patientInfo() {
        if (this._uiState.patientId === null)
            return null;
        return {
            patientId: this._uiState.patientId,
        };
    }
    get minimumLoadingTimeHasPassed() {
        if (this.authManager.authenticatedWithIAL2)
            return true;
        if (this.loadingStartTime === null)
            return true;
        if (!this._uiState.now)
            return true;
        return this._uiState.now.getTime() - this.loadingStartTime > 10 * 1000;
    }
    get isLoading() {
        if (this._uiState.isStatic)
            return false;
        if (!this.fastenLoaded)
            return true;
        if (!this.minimumLoadingTimeHasPassed)
            return true;
        if (!!this.retrieval && this.retrieval.medAlliesGetDocumentsComplete && this.retrieval.medAlliesGetEndpointsComplete) {
            return false;
        }
        if (this.retrievalError) {
            return false;
        }
        return true;
    }
    get isMedAlliesError() {
        return !this.needsToReauthWithCLEAR && this.retrievalError && this.zeroLocationsFound;
    }
    get currentStep() {
        if (this._uiState.stepToShow !== undefined) {
            return this._uiState.stepToShow;
        }
        if (this.isScreenshotMode) {
            return this.anyLocationNotAddedViaFasten ? DocumentRetrievalStep.RECORDS_FOUND : DocumentRetrievalStep.RECORDS_NOT_FOUND;
        }
        if (this.patientConsentUIManager.session.patientConsentSessionState !== PatientConsentSessionState.INITIALIZED) {
            return DocumentRetrievalStep.RESTORING_SESSION;
        }
        if (this.isLoading) {
            return DocumentRetrievalStep.LOADING;
        }
        if (this.needsToReauthWithCLEAR || this.isMedAlliesError) {
            return DocumentRetrievalStep.TECHNICAL_ERROR;
        }
        if (this.zeroLocationsFound && !this.needsToReauthWithCLEAR && !this._uiState.hasSeenRecordsNotFoundScreen) {
            return DocumentRetrievalStep.RECORDS_NOT_FOUND;
        }
        if (this._uiState.hasDeclinedSharing) {
            return DocumentRetrievalStep.CONTINUE_WITHOUT_SHARING;
        }
        if (!this._uiState.hasSeenRecordsFoundScreen && !this.zeroLocationsFound) {
            return DocumentRetrievalStep.RECORDS_FOUND;
        }
        if (this.isInHurLoading) {
            return DocumentRetrievalStep.HUR_LOADING;
        }
        if (this._uiState.currentLocationKeyForLogin !== null) {
            return DocumentRetrievalStep.LOCATION_LOGIN;
        }
        return DocumentRetrievalStep.REVIEW_AND_SHARE;
    }
    get isIAS() {
        return this.patientConsentUIManager.isIAS;
    }
    continueFromRecordsFound() {
        this._uiState.hasSeenRecordsFoundScreen = true;
    }
    continueFromRecordsNotFound() {
        this._uiState.hasDeclinedSharing = true;
        this._uiState.hasSeenRecordsNotFoundScreen = true;
    }
    continueWithoutSharingRecords() {
        this._uiState.hasDeclinedSharing = true;
    }
    async confirmContinueWithoutSharing() {
        if (this._uiState.pendingSkipLocationKey && this.retrieval) {
            this._uiState.skippedLoginKeys.push(this._uiState.pendingSkipLocationKey);
            await this.documentRetrievalManager.skipLocation(this.retrieval.batchId, this._uiState.pendingSkipLocationKey);
            this._uiState.pendingSkipLocationKey = null;
            this._uiState.hasDeclinedSharing = false;
            this.moveToNextLoginLocation();
        }
        else {
            if (this.onRecordsShared) {
                await this.onRecordsShared();
            }
        }
    }
    continueFromReviewAndShare(locationList) {
        const locations = locationList !== null && locationList !== void 0 ? locationList : this.locationList;
        const loginRequired = locations.filter((loc) => loc.status === DocumentRetrievalLocationStatus.NEEDS_AUTH || loc.status === DocumentRetrievalLocationStatus.SKIPPED);
        this._uiState.loginRequiredLocationKeys = loginRequired.map((loc) => loc.key);
        this._uiState.currentLoginIndex = 0;
        this._uiState.isInLoginCycle = true;
        if (this._uiState.loginRequiredLocationKeys.length > 0) {
            this._uiState.currentLocationKeyForLogin = this._uiState.loginRequiredLocationKeys[0];
        }
    }
    retryLocations(locations) {
        this._uiState.loginRequiredLocationKeys = locations.map((loc) => loc.key);
        this._uiState.currentLoginIndex = 0;
        this._uiState.isInLoginCycle = true;
        if (this._uiState.loginRequiredLocationKeys.length > 0) {
            this._uiState.currentLocationKeyForLogin = this._uiState.loginRequiredLocationKeys[0];
        }
    }
    onLoginSuccess() {
        this.moveToNextLoginLocation();
    }
    async onLoginSkipped() {
        if (!this._uiState.currentLocationKeyForLogin)
            return;
        const isWaitingForAuth = this._uiState.currentLocationKeyForLogin === this.facilitatedFhirAuthInProgress;
        if (isWaitingForAuth) {
            this._uiState.pendingSkipLocationKey = this._uiState.currentLocationKeyForLogin;
            this.facilitatedFHIRAuthManager.resetAuth();
            this.continueWithoutSharingRecords();
        }
        else {
            if (this.retrieval) {
                this._uiState.skippedLoginKeys.push(this._uiState.currentLocationKeyForLogin);
                await this.documentRetrievalManager.skipLocation(this.retrieval.batchId, this._uiState.currentLocationKeyForLogin);
            }
            this.moveToNextLoginLocation();
        }
    }
    moveToNextLoginLocation() {
        this._uiState.currentLoginIndex++;
        const loginKeysLength = this._uiState.loginRequiredLocationKeys.length;
        if (this._uiState.currentLoginIndex < loginKeysLength) {
            this._uiState.currentLocationKeyForLogin = this._uiState.loginRequiredLocationKeys[this._uiState.currentLoginIndex];
        }
        else {
            this._uiState.isInLoginCycle = false;
            this._uiState.currentLocationKeyForLogin = null;
            const completedCount = this._uiState.completedLoginKeys.length;
            const skippedCount = this._uiState.skippedLoginKeys.length;
            const hasAnyCompletedLocations = this.nonLoginRequiredLocations.length > 0 || completedCount > 0;
            const allLoginCycleLocationsSkipped = completedCount === 0 && skippedCount === loginKeysLength;
            if (!hasAnyCompletedLocations && allLoginCycleLocationsSkipped) {
                this._uiState.hasDeclinedSharing = true;
            }
            else {
                this._uiState.hasSeenRecordsFoundScreen = true;
            }
        }
    }
    get currentLocationForLogin() {
        if (!this._uiState.currentLocationKeyForLogin || !this.retrieval)
            return null;
        return this.retrieval.locations.find((loc) => loc.id === this._uiState.currentLocationKeyForLogin);
    }
    clearCurrentLocationForLogin() {
        this._uiState.currentLocationKeyForLogin = null;
        this._uiState.isInLoginCycle = false;
    }
    get hasCompletedLoginCycle() {
        const loginKeysLength = this._uiState.loginRequiredLocationKeys.length;
        return loginKeysLength > 0 && !this._uiState.isInLoginCycle && this._uiState.currentLocationKeyForLogin === null;
    }
    get hasMoreLocationsInLoginCycle() {
        var _a;
        const currentIndex = (_a = this._uiState.currentLoginIndex) !== null && _a !== void 0 ? _a : 0;
        const loginKeysLength = this._uiState.loginRequiredLocationKeys.length;
        return currentIndex + 1 < loginKeysLength;
    }
    get loginRequiredLocations() {
        return this.locationList.filter((loc) => loc.status === DocumentRetrievalLocationStatus.NEEDS_AUTH || loc.status === DocumentRetrievalLocationStatus.SKIPPED);
    }
    get nonLoginRequiredLocations() {
        return this.locationList.filter((loc) => !(loc.status === DocumentRetrievalLocationStatus.NEEDS_AUTH || loc.status === DocumentRetrievalLocationStatus.SKIPPED));
    }
    get allLocationsRequireLogin() {
        return this.loginRequiredLocations.length > 0 && this.nonLoginRequiredLocations.length === 0;
    }
    get anyLocationNeedsAuth() {
        return this.locationList.some((loc) => loc.status === DocumentRetrievalLocationStatus.NEEDS_AUTH);
    }
    get anyLocationAddedViaFasten() {
        return this.locationList.some((loc) => loc.authType === DocumentRetrievalAuthType.FASTEN);
    }
    get anyLocationNotAddedViaFasten() {
        return this.locationList.some((loc) => loc.authType !== DocumentRetrievalAuthType.FASTEN);
    }
    get recordsFoundCount() {
        if (!this.retrieval)
            return 0;
        return this.retrieval.documents.length;
    }
    get locationsFoundCount() {
        if (!this.retrieval)
            return 0;
        return this.retrieval.locations.length;
    }
    get loadingProgress() {
        if (!this._uiState.now || !this._uiState.loadingStart)
            return 0.05;
        const expectedLoadingDuration = 30;
        const currentDuration = (this._uiState.now.getTime() - this._uiState.loadingStart.getTime()) / 1000;
        let progress = currentDuration / expectedLoadingDuration;
        progress = progress < 0 ? 0 : progress > 1 ? 1 : progress;
        progress = 0.05 + progress * 0.9;
        return progress;
    }
    get loadingTitle() {
        return t("Finding your records...");
    }
    get loadingSubTitle() {
        if (!this._uiState.now || !this._uiState.loadingStart) {
            return t("Connecting securely to your providers");
        }
        const elapsed = (this._uiState.now.getTime() - this._uiState.loadingStart.getTime()) / 1000;
        if (elapsed < 0) {
            return t("Connecting securely to your providers");
        }
        const cyclePosition = Math.floor(elapsed / 3) % 3;
        switch (cyclePosition) {
            case 0:
                return t("Connecting securely to your providers");
            case 1:
                return t("Retrieving your health records");
            case 2:
            default:
                return t("This may take up to a minute");
        }
    }
    get fastenLoaded() {
        if (this._uiState.isStatic)
            return true;
        return this.fastenLoaderService.fastenLoaded;
    }
    get fastenPublicId() {
        return this.isProductionRetrieveDataEnabled
            ? this.environment.FASTEN_PRODUCTION_PUBLIC_ID
            : this.environment.FASTEN_NONPRODUCTION_PUBLIC_ID;
    }
    get fastenPatientId() {
        var _a, _b;
        return (_b = (_a = this.patientInfo) === null || _a === void 0 ? void 0 : _a.patientId) !== null && _b !== void 0 ? _b : "";
    }
    get isLoggingInWithCLEAR() {
        return this.smartAuthManager.state === SmartAuthState.IN_PROGRESS || this.smartAuthManager.state === SmartAuthState.LOGGING_IN;
    }
    get clearLoginError() {
        if (!this.smartAuthManager.errorReason)
            return null;
        switch (this.smartAuthManager.errorReason) {
            case SmartAuthErrorReason.LOGIN_WINDOW_CLOSED:
                return t("The login window was closed, please try again.");
            case SmartAuthErrorReason.AUTH_FAILED:
                return t("The login failed, please try again.");
            case SmartAuthErrorReason.CLEAR_VALIDATION_FAILED:
                return t("We could not validate your identity, please try again");
            default:
                return t("An unexpected error occurred while logging in with CLEAR, please try again.");
        }
    }
    get animate() {
        return !this.patientConsentUIManager.isScreenshotMode;
    }
    startLoadingTimer() {
        this._uiState.loadingStart = new Date();
        this._uiState.now = new Date();
        this.loadingInterval = setInterval(() => {
            this.updateTime();
        }, 500);
    }
    clearLoadingTimer() {
        clearInterval(this.loadingInterval);
    }
    updateTime() {
        this._uiState.now = new Date();
    }
    statusTextForLocation(location) {
        switch (location.status) {
            case DocumentRetrievalLocationStatus.COMPLETE:
            case DocumentRetrievalLocationStatus.ERROR: {
                return t("Records retrieved");
            }
            case DocumentRetrievalLocationStatus.SKIPPED:
                return t("You chose to skip records from this provider");
            case DocumentRetrievalLocationStatus.NEEDS_AUTH:
                return t("Login needed to include records from this provider");
            default:
                return t("Retrieving records...");
        }
    }
    async reauth() {
        void this.patientConsentUIManager.reAuth();
    }
    async retryMedAlliesRetrieval() {
        var _a;
        if (!((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId))
            return;
        await this.documentRetrievalManager.retryDocumentRetrieval(this.retrieval.batchId);
    }
    async skipMedAlliesRetrieval() {
        var _a;
        if (!((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.batchId))
            return;
        await this.documentRetrievalManager.skipDocumentRetrieval(this.retrieval.batchId);
    }
    async skipLocation(locationKey) {
        if (!this.retrieval)
            return;
        await this.documentRetrievalManager.skipLocation(this.retrieval.batchId, locationKey);
    }
    async loginToLocation(locationKey) {
        await this.patientConsentUIManager.loginToLocationAndSaveState(locationKey, this._uiState);
    }
    restoreDocumentRetrievalUIState() {
        const documentRetrievalUIState = this.patientConsentUIManager.getDocumentRetrievalUIState();
        if (documentRetrievalUIState) {
            this._uiState = plainToInstance(DocumentRetrievalUIState, documentRetrievalUIState);
        }
    }
    async handleFastenCallback(detail) {
        try {
            const fastenEvent = JSON.parse(detail);
            switch (fastenEvent.event_type) {
                case FastenCallbackEventType.CONNECTION_PENDING:
                    return;
                case FastenCallbackEventType.CONNECTION_SUCCESS:
                    break;
                case FastenCallbackEventType.CONNECTION_FAILED:
                    return;
                case FastenCallbackEventType.WIDGET_COMPLETE:
                    return;
            }
            if (fastenEvent.event_type === FastenCallbackEventType.CONNECTION_SUCCESS) {
                if (fastenEvent.data.external_id !== this.fastenPatientId) {
                    console.warn(`Unmatched patient id:${fastenEvent.data.external_id} and ${this.fastenPatientId}`);
                    return;
                }
                if (!this.retrieval) {
                    console.warn(`No retrieval found`);
                    return;
                }
                await this.documentRetrievalManager.getFastenDocuments(this.retrieval.batchId, fastenEvent.data.org_connection_id, fastenEvent.data.portal_id, fastenEvent.data.brand_id);
                this._uiState.hasSeenRecordsFoundScreen = true;
                this._uiState.hasDeclinedSharing = false;
            }
        }
        catch (error) {
            console.warn(`Fasten callback error: ${error}`);
        }
    }
    get hurLoadingTitle() {
        return this.isIAS ? t("Building your HealthEx Unified Record...") : t("Sharing records and making a copy for you...");
    }
    get hurLoadingSubtitle() {
        var _a;
        if (this.isIAS) {
            const preferences = this.patientPreferencesManager.preferences.value;
            if (preferences === NOT_LOADED)
                return "";
            const preferredContactMethod = (_a = preferences === null || preferences === void 0 ? void 0 : preferences.preferredContactMethod) !== null && _a !== void 0 ? _a : ContactMethod.EMAIL;
            if (preferredContactMethod === ContactMethod.EMAIL) {
                return t("You can leave this page and once completed we'll email a copy of your records to {{ email }}.", {
                    email: this.authManager.email,
                });
            }
            else if (preferredContactMethod === ContactMethod.TEXT) {
                return t("You can leave this page and once completed we'll send a copy of your records to {{ phone }}.", {
                    phone: this.authManager.formattedPhone,
                });
            }
            else {
                return t("You can leave this page and check back later.");
            }
        }
        else {
            return t("We’re building your HealthEx Unified Record — your personal copy of everything we found.");
        }
    }
    get hurLoadingTimeAtMax() {
        var _a;
        const isFasten = (_a = this.patientConsentUIManager.retrieval) === null || _a === void 0 ? void 0 : _a.locations.some((location) => location.source === DocumentRetrievalTaskSource.FASTEN);
        const finalizeTime = isFasten ? 180 : 60;
        const vectorizationTime = this.isAIFlow ? 120 : 0;
        return finalizeTime + vectorizationTime;
    }
    hurLoadingGoToHealthEx() {
        this.patientConsentUIManager.close();
    }
}
__decorate([
    observable,
    __metadata("design:type", DocumentRetrievalUIState)
], DocumentRetrievalViewModel.prototype, "_uiState", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "retrievalError", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isAIFlow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isVectorizationInProgress", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isVectorizationComplete", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "canAddLocations", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "zeroLocationsFound", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "explanationText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "needsToReauthWithCLEAR", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "getAuthErrorText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "locationList", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "locationStatusCounts", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isInHurLoading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isProductionRetrieveDataEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "facilitatedFhirAuthInProgress", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isfacilitatedFhirAuthSuccessful", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "facilitatedFhirAuthError", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "facilitatedFhirAuthErrorMessage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "patientInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "minimumLoadingTimeHasPassed", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isLoading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isMedAlliesError", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "currentStep", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isIAS", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "currentLocationForLogin", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "hasCompletedLoginCycle", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "hasMoreLocationsInLoginCycle", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "loginRequiredLocations", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "nonLoginRequiredLocations", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "allLocationsRequireLogin", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "anyLocationNeedsAuth", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "anyLocationAddedViaFasten", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "anyLocationNotAddedViaFasten", null);
__decorate([
    computed,
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "recordsFoundCount", null);
__decorate([
    computed,
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "locationsFoundCount", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "loadingProgress", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "loadingTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "loadingSubTitle", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "fastenLoaded", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "fastenPublicId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "fastenPatientId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "isLoggingInWithCLEAR", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "clearLoginError", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "animate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "hurLoadingTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "hurLoadingSubtitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalViewModel.prototype, "hurLoadingTimeAtMax", null);
